import type { ExtensionManifest } from '@atlaskit/editor-common/extensions/extension-manifest';
import type { ExtensionProvider } from '@atlaskit/editor-common/extensions/extension-provider';
import DefaultExtensionProvider from '@atlaskit/editor-common/extensions/default-extension-provider';
import combineExtensionProviders from '@atlaskit/editor-common/extensions/combine-extension-providers';
import type ProviderFactory from '@atlaskit/editor-common/provider-factory/provider-factory';
import type { EditorContentMode } from '@atlaskit/editor-common/types/editor-appearance';
import { type IntlShape } from 'react-intl';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import type { ExtensionHandlerWithReferenceFn } from '@atlassian/editor-referentiality/types';

import {
	createForgeMacroExtensionData,
	createForgeUIAnalyticsContext,
	environmentToForgeProductEnvironment,
} from '@confluence/fabric-providers/entry-points/ForgeProvider.ts';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient.ts';
import { getRelayEnvironment } from '@confluence/graphql-provider/entry-points/relayEnvironment.ts';

import { getConfluenceMacrosExtensionProviderForRenderer } from '@confluence/fabric-extension-lib/entry-points/getConfluenceMacrosExtensionProviderForRenderer.ts';
import { EXTENSION_TYPE } from '@confluence/fabric-extension-lib/entry-points/extensionConstants.ts';
import type { RendererExtensionHandlers } from '@confluence/fabric-extension-lib/entry-points/extensionTypes.ts';

import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { getLogger } from '@confluence/logger';
import { fetchForgeExtensionProvider } from '@confluence/fabric-extension-handlers/entry-points/getForgeExtensionHandler.ts';
import { createRendererMediaProvider } from '@confluence/fabric-media-support/entry-points/MediaProvider.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { getWorkspaceARI } from '@confluence/forge-ui/entry-points/useWorkspaceARI.ts';
import { FlagProvider } from '@confluence/forge-ui/entry-points/flagProvider.ts';
import { createForgeMacroExtensionHandler } from '@confluence/forge-ui/entry-points/ForgeMacro.ts';

import {
	openMacroBrowserForInitialConfiguration,
	openMacroBrowserToEditExistingMacro,
} from '../../lib/editor-extensions/index.ts';
import {
	TemplateVariableMode,
	getTemplateVariableExtensionManifest,
} from '../../lib/editor-extensions/template-variable-extension/manifest.tsx';
import { getMigrationExtensionManifest } from '../../lib/extensions-common/migration-extension/manifest.tsx';

import { useGetContextToken } from '@confluence/forge-ui/entry-points/useContextToken.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';

export type ExtensionProviderOptions = {
	intl: IntlShape;
	containerId: string; // spaceKey in Next
	accountId?: string | null; // userId in Next
	contentId: string;
	cloudId: string;
	spaceKey?: string;
	spaceId?: string;
	contentType?: string;
	contentVersion?: number;
	contentStatus?: string;
	contentMode?: EditorContentMode;
	environment: string; // comes from sessionData
	featureFlags?: { [flag: string]: any }; // comes from sessionData
	extensionHandlers: RendererExtensionHandlers;
	showTemplateInputInPreview?: boolean;
	showTemplateVariablesInPreview?: boolean;
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
	activationId: string;
	isLivePage?: boolean;
	hasLegacyContentMacro?: boolean;
};

const createLCMRendererExtensionProvider = async (
	hasLegacyContentMacro: boolean,
	contentMode?: EditorContentMode,
) => {
	const { rendererManifest, rendererManifestSsr } = await import(
		/* webpackChunkName: "loadable-legacy-content-macro" */ '@confluence/experiment-legacy-content-macro/entry-points/rendererManifests.ts'
	);
	return new DefaultExtensionProvider([
		(hasLegacyContentMacro
			? rendererManifestSsr(contentMode)
			: rendererManifest) as unknown as ExtensionManifest,
	]);
};

type MigratedMacroExtensionProvider = ExtensionProvider & {
	getMigratedFromExtension: (type: string, key: string) => Promise<ExtensionManifest | undefined>;
	getPreloadedMigratedFromExtension: (type: string, key: string) => ExtensionManifest | undefined;
};

const isMigratedMacroExtensionProvider = (
	provider: ExtensionProvider,
): provider is MigratedMacroExtensionProvider =>
	'getMigratedFromExtension' in provider && 'getPreloadedMigratedFromExtension' in provider;

const createMigratedMacroExtensionResolver = (
	forgeExtensionProvider: Promise<ExtensionProvider>,
) => {
	let resolvedForgeExtensionProvider: MigratedMacroExtensionProvider | undefined;
	void forgeExtensionProvider.then(
		(provider) => {
			if (isMigratedMacroExtensionProvider(provider)) {
				resolvedForgeExtensionProvider = provider;
			}
		},
		() => undefined,
	);

	return {
		resolveExtension: async (type: string, key: string) => {
			if (type === EXTENSION_TYPE.MACRO_CORE) {
				try {
					const provider = await forgeExtensionProvider;
					if (isMigratedMacroExtensionProvider(provider)) {
						const migratedExtension = await provider.getMigratedFromExtension(type, key);
						if (migratedExtension) {
							return migratedExtension;
						}
					}
				} catch {
					return undefined;
				}
			}
		},
		resolvePreloadedExtension: (type: string, key: string) => {
			if (type === EXTENSION_TYPE.MACRO_CORE && resolvedForgeExtensionProvider) {
				const migratedExtension = resolvedForgeExtensionProvider.getPreloadedMigratedFromExtension(
					type,
					key,
				);
				if (migratedExtension) {
					return migratedExtension;
				}
			}
		},
	};
};

/**
 * Populate Forge-based extension providers
 *
 * @param providerFactory provider factory to add extension providers into
 * @param options options to initialize extension providers
 */
export const populateExtensionProviders = (
	providerFactory: ProviderFactory,
	{
		intl,
		containerId,
		accountId,
		cloudId,
		spaceKey,
		spaceId,
		contentId,
		contentType,
		contentVersion,
		contentStatus,
		contentMode,
		environment,
		extensionHandlers,
		showTemplateInputInPreview,
		showTemplateVariablesInPreview,
		createAnalyticsEvent,
		activationId,
		isLivePage = false,
		hasLegacyContentMacro = false,
	}: ExtensionProviderOptions,
): Promise<void> => {
	const extensionProvidersList: (Promise<ExtensionProvider> | ExtensionProvider)[] = [];

	const extensionProvider = getConfluenceMacrosExtensionProviderForRenderer(
		{ contentId, spaceKey, spaceId, contentType, requestingUserId: accountId, features: {} },
		extensionHandlers,
		getMigrationExtensionManifest,
		openMacroBrowserToEditExistingMacro,
		openMacroBrowserForInitialConfiguration,
		intl,
		createAnalyticsEvent,
		providerFactory,
		isLivePage,
	);

	const lcmExtension = createLCMRendererExtensionProvider(hasLegacyContentMacro, contentMode);
	extensionProvidersList.push(extensionProvider, lcmExtension);

	if (showTemplateInputInPreview || showTemplateVariablesInPreview) {
		extensionProvidersList.push(
			new DefaultExtensionProvider([
				getTemplateVariableExtensionManifest(
					intl,
					showTemplateInputInPreview ? TemplateVariableMode.INPUT : TemplateVariableMode.CONFIG,
				) as ExtensionManifest<any>,
			]),
		);
	}

	const apolloClient = getAGGClient();
	const contextIds = [getWorkspaceARI({ cloudId, activationId })];

	const forgeExtensionProvider = async () => {
		const nestedAdfMediaProvider = expValEquals(
			'platform_media_token_nested_macro',
			'isEnabled',
			true,
		)
			? createRendererMediaProvider({
					contentId,
					spaceKey,
					isExternalShare: false,
					resolveContentIdFromCollection: true,
				})
			: undefined;

		const getProviderOptions = ({
			spaceKey: spaceKeyArg,
			spaceId: spaceIdArg,
		}: {
			spaceKey: string;
			spaceId?: string;
		}) => ({
			accountId: accountId ?? undefined,
			cloudId,
			apolloClient,
			relayEnvironment: getRelayEnvironment(),
			contextIds,
			dataProviders: providerFactory,
			...(nestedAdfMediaProvider ? { nestedAdfMediaProvider } : {}),
			environment: environmentToForgeProductEnvironment(environment),
			product: 'confluence',
			page: 'confluence:macroRenderer',
			isEditing: false,
			contentMode,
			analyticsWebClient: getAnalyticsWebClient(),
			forgeUIAnalyticsContext: createForgeUIAnalyticsContext({
				objectId: contentId,
				// @ts-ignore
				objectType: contentType,
				containerId,
				containerType: 'space',
			}),
			extensionData: {
				...createForgeMacroExtensionData({
					spaceKey: spaceKeyArg ?? '',
					isLivePage,
					spaceId: spaceIdArg ?? spaceId ?? '',
					contentId,
					contentType,
					contentVersion: contentVersion ?? undefined,
					contentStatus: contentStatus ?? undefined,
				}),
			},
			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
			extensionHandlerWithReference: (fg('conf_frontend_forge_static_macro_mvp')
				? createForgeMacroExtensionHandler(false)
				: extensionHandlers[
						EXTENSION_TYPE.MACRO_WITH_REFERENCES
					]) as ExtensionHandlerWithReferenceFn<any>,
			connectExtensionProvider: extensionProvider,
			FlagProvider,
			extensionsFilter: undefined,
			intl,
			locale: intl.locale,
			useGetContextToken,
			useRouteActions,
			productExtensionHandlers: extensionHandlers,
			isMigratedFromAliasEnabled: true,
		});

		return fetchForgeExtensionProvider({
			getProviderOptions,
			spaceKey,
		});
	};

	const forgeExtensionProviderPromise = forgeExtensionProvider();
	extensionProvidersList.push(forgeExtensionProviderPromise);

	const combinedProvider = combineExtensionProviders(
		extensionProvidersList,
		createMigratedMacroExtensionResolver(forgeExtensionProviderPromise),
	);
	// oxlint-disable-next-line compat/compat
	providerFactory.setProvider('extensionProvider', Promise.resolve(combinedProvider));

	// Call preload on all provider that later we can use it synchronously in packages/editor/editor-common/src/extensions/extension-handlers.tsx
	const manifestPreload = combinedProvider.preload();
	// Callers are free to ignore the returned preload, so always keep a rejection handler attached.
	void manifestPreload.catch((error) => {
		getLogger('populateExtensionProviders').error`Failed to preload extension manifests: ${error}`;
	});

	return manifestPreload;
};
